import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMusic } from "@/contexts/MusicContext";
import { useUserVerification } from "@/components/profile/hooks/useUserVerification";
import { useIsMobile } from "@/hooks/useResponsive";
import { hapticFeedback } from "@/utils/mobileInteractions";
import { cn } from "@/lib/utils";
import { useBrandingAssets } from "@/hooks/useBrandingAssets";
import { useVerificationBannerVisible } from "@/components/verification/VerificationUpgradeBanner";

const STORAGE_KEY = "floating-verification-bubble-pos";

export function FloatingVerificationBubble() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole } = useAuth();
  const { isPlayerExpanded } = useMusic();
  const { isArtistVerified, isVerificationLoading } = useUserVerification(user);
  const isMobile = useIsMobile();
  const { faviconUrl } = useBrandingAssets();
  const isBannerVisible = useVerificationBannerVisible();

  // Restore saved position
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { x: sx, y: sy } = JSON.parse(saved);
        if (typeof sx === "number" && typeof sy === "number") {
          x.set(sx);
          y.set(sy);
        }
      }
    } catch {}
  }, [x, y]);

  // Auto-close timer
  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
        setShowMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  // Don't show if not authenticated or on verification pages
  if (!user || !userRole || isVerificationLoading) {
    return null;
  }

  // Hide when the verification upgrade banner is already showing
  if (isBannerVisible) {
    return null;
  }

  // Only show for artists and sponsors who are not verified
  const shouldShow =
    (userRole === "artist" || userRole === "sponsor") && !isArtistVerified;

  // Hide on verification related pages
  const isOnVerificationPage =
    location.pathname.includes("/verification") ||
    location.pathname.includes("/verify");

  if (!shouldShow || isOnVerificationPage) {
    return null;
  }

  const handleBubbleClick = () => {
    // Suppress click if user just dragged
    if (isDragging) return;
    if (isMobile) {
      hapticFeedback.medium();
    }

    if (!isExpanded) {
      setIsExpanded(true);
      setTimeout(() => setShowMessage(true), 200);
    } else {
      setIsExpanded(false);
      setShowMessage(false);
    }
  };

  const handleVerifyClick = () => {
    if (isMobile) {
      hapticFeedback.heavy();
    }
    navigate("/verification");
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-[55] pointer-events-none transition-all duration-300",
        isMobile ? "left-4" : "left-8",
        isPlayerExpanded
          ? isMobile
            ? "bottom-[420px]" // Mobile: above expanded player
            : "bottom-[200px]" // Desktop: above expanded player
          : isMobile
            ? "bottom-24" // Mobile: above bottom nav
            : "bottom-8", // Desktop: above fixed player
      )}
    >
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={
            !isMobile
              ? {
                  scale: [1, 1.1, 1],
                  opacity: 1,
                }
              : { scale: 1, opacity: 1 }
          }
          exit={{ scale: 0, opacity: 0 }}
          transition={
            !isMobile
              ? {
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  scale: {
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  },
                }
              : { type: "spring", stiffness: 300, damping: 20 }
          }
          className="relative pointer-events-auto"
          style={{ x, y }}
        >
          {/* Main bubble */}
          <motion.button
            onClick={handleBubbleClick}
            drag
            dragMomentum={false}
            dragElastic={0.1}
            style={{ touchAction: "none" }}
            onDragStart={(e) => {
              const point = "touches" in e ? e.touches[0] : (e as MouseEvent);
              dragStartPos.current = { x: point.clientX, y: point.clientY };
            }}
            onDrag={(e, info) => {
              if (!dragStartPos.current) return;
              if (Math.abs(info.offset.x) > 5 || Math.abs(info.offset.y) > 5) {
                setIsDragging(true);
              }
              // Mirror drag onto parent's motion values so position persists
              x.set(x.get() + info.delta.x);
              y.set(y.get() + info.delta.y);
            }}
            onDragEnd={() => {
              try {
                localStorage.setItem(
                  STORAGE_KEY,
                  JSON.stringify({ x: x.get(), y: y.get() }),
                );
              } catch {}
              setTimeout(() => {
                setIsDragging(false);
                dragStartPos.current = null;
              }, 50);
            }}
            // Keep button itself anchored; drag offset lives on parent via x/y
            dragSnapToOrigin
            className={cn(
              "relative flex items-center bg-white rounded-full border-2 border-gray-100 backdrop-blur-sm transition-all duration-300",
              isDragging ? "cursor-grabbing" : "cursor-grab",
              isExpanded
                ? isMobile
                  ? "px-4 py-3 min-w-[200px]"
                  : "px-5 py-4 min-w-[240px]"
                : isMobile
                  ? "w-14 h-14 justify-center hover:scale-105 active:scale-95"
                  : "w-16 h-16 justify-center hover:scale-105 active:scale-95",
            )}
            animate={
              !isMobile
                ? {
                    y: [0, -4, 0],
                    rotate: [5, -5, 5],
                    boxShadow: [
                      "0 10px 30px rgba(139, 92, 246, 0.3)",
                      "0 15px 35px rgba(139, 92, 246, 0.4)",
                      "0 10px 30px rgba(139, 92, 246, 0.3)",
                    ],
                  }
                : {
                    y: [0, -4, 0],
                    boxShadow: [
                      "0 10px 30px rgba(139, 92, 246, 0.3)",
                      "0 15px 35px rgba(139, 92, 246, 0.4)",
                      "0 10px 30px rgba(139, 92, 246, 0.3)",
                    ],
                  }
            }
            transition={{
              y: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
              rotate: !isMobile
                ? {
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }
                : undefined,
              boxShadow: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Pulsing background */}
            <motion.div
              className="absolute inset-0 bg-primary/20 rounded-full"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Favicon */}
            <motion.img
              src={faviconUrl}
              alt="PayStreem"
              className={cn(
                "relative z-10 object-contain",
                isExpanded
                  ? "w-6 h-6 mr-2 flex-shrink-0"
                  : isMobile
                    ? "w-8 h-8"
                    : "w-10 h-10",
              )}
              animate={
                isExpanded
                  ? {}
                  : {
                      rotate: [0, 5, -5, 0],
                    }
              }
              transition={{
                rotate: {
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            />

            {/* Expanded message */}
            <AnimatePresence>
              {showMessage && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center space-x-2"
                >
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-semibold text-gray-800">
                      Get Verified!
                    </span>
                    <span className="text-xs text-gray-600">
                      {userRole === "artist"
                        ? "Unlock sponsored streams"
                        : "Start sponsoring artists"}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Quick CTA button when expanded */}
          <AnimatePresence>
            {isExpanded && showMessage && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                transition={{ delay: 0.1, duration: 0.2 }}
                onClick={handleVerifyClick}
                className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all duration-200"
                whileTap={{ scale: 0.95 }}
              >
                Verify Now
              </motion.button>
            )}
          </AnimatePresence>

          {/* Notification dot */}
          {!isExpanded && (
            <motion.div
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
